import React, { Fragment, useState } from "react";
import { Divider, Form, Message, Checkbox, CheckboxProps } from "semantic-ui-react";
import { IAuditNoteFormData } from "../../interfaces/IAuditNote";
import { useTranslation } from "react-i18next";

import { NoteFormInfo } from "./NoteFormInfo";
import { useNotification } from "../../../sg-core/hooks/useToast";
import { IFormSchema } from "../../interfaces/IFormSchema";
import { ObservationFormCategoryIcon } from "../observation/ObservationFormCategoryIcon ";
import ErrorBoundary from "../ErrorBoundary";
import { RenderObservationFormDefinition } from "../observation/RenderObservationFormDefinition";
import { useObservationContext } from "../../data/observations/useObservationContext";
import IFormDefinition from "../../interfaces/IFormDefinition";
import { ObservationForm } from "../observation/ObservationForm";
import { DefaultNoteForm } from "./AuditNoteForm/DefaultNoteForm";
import defaultNoteSchema from "../../constants/note.schema.json";
import { TFunction } from "i18next";
import IObservation from "../../interfaces/IObservation";

const getTextProps = (t: TFunction) => ({
    addNote: t("Lisää huomio"),
    save: t("Tallenna"),
    cancel: t("Peruuta"),
    saveError: t("Tallennus epäonnistui"),
    failedToFetchObservationForm: t("Havainto lomakkeen haku epäonnistui!"),
    sendToObservationApp: t("Lähetä havaintosovellukseen"),
    makeItAsObservationDescription: t(
        "Havainnot lähetetään, kun koko auditointi merkitään valmiiksi. Siihen asti niitä voi muokata tai poistaa. Lähetyksen jälkeen havaintoja voi käsitellä havaintosovelluksen puolella.",
    ),
    cantEditObservationDescription: t(
        "Havainto on lähetetty havaintosovellukseen, lähetettyä havaintoa ei voi enää muokata.",
    ),

    failedToFetchForm: t("Lomakkeen haku epäonnistui!"),
});

interface EditableAuditNoteFormContentProps extends ReturnType<typeof getTextProps> {
    auditFormType: number;
    observationFormType?: string;
    observationFormDefinition?: IFormDefinition;
    formData: IAuditNoteFormData;
    onUpdateFormData: (values: IAuditNoteFormData) => void;
    onSubmit: (values: IAuditNoteFormData, observationSchema?: IFormSchema) => void;
    submitButton: React.RefObject<HTMLButtonElement>;
    hasRelatedObservation: boolean;
    initialFormData: IAuditNoteFormData;
}

export const EditableAuditNoteFormContent: React.FC<EditableAuditNoteFormContentProps> = ({
    observationFormDefinition: formDefinition,
    observationFormType,
    auditFormType,
    formData,
    onUpdateFormData: updateFormData,
    submitButton,
    onSubmit: submit,
    hasRelatedObservation,
    initialFormData,
    ...texts
}) => {
    const updateMakeObservation: CheckboxProps["onChange"] = (_1, { checked }) => {
        updateFormData({
            makeObservation: checked,
            target: initialFormData.target,
            sijainti: initialFormData.target,
        });
    };

    return (
        <Fragment>
            <NoteFormInfo auditFormType={auditFormType} formData={formData} />

            {formData.makeObservation && formDefinition && !hasRelatedObservation ? (
                <Fragment>
                    <ObservationFormCategoryIcon categoryId={formDefinition.category} />
                    {formDefinition.name}
                    <Divider hidden />
                    <Form as="div">
                        <ObservationForm
                            formData={formData}
                            onChange={updateFormData}
                            formDefinition={formDefinition}
                            onSubmit={submit}
                            submitButton={submitButton}
                        />
                    </Form>
                </Fragment>
            ) : (
                <Fragment>
                    <Divider hidden />
                    <Form as="div">
                        <DefaultNoteForm
                            formData={formData}
                            onChange={updateFormData}
                            schema={defaultNoteSchema as any}
                            onSubmit={submit}
                            submitButton={submitButton}
                        />
                    </Form>
                </Fragment>
            )}
            <Divider />
            {observationFormType ? (
                <Form.Field disabled={hasRelatedObservation}>
                    <Checkbox
                        className="NoteForm-makeItObservationCheckbox"
                        label={texts.sendToObservationApp}
                        style={{ paddingBottom: "0px", marginBottom: "0px" }}
                        checked={formData.makeObservation}
                        onChange={updateMakeObservation}
                        disabled={hasRelatedObservation}
                    />
                    <div className="NoteForm-makeItObservationDescription">
                        <small>
                            {" "}
                            {hasRelatedObservation
                                ? texts.cantEditObservationDescription
                                : texts.makeItAsObservationDescription}{" "}
                        </small>
                    </div>
                </Form.Field>
            ) : null}
        </Fragment>
    );
};

interface EditableAuditNoteFormProps {
    initialFormData: IAuditNoteFormData;
    auditFormType: number;
    observationFormType?: string;
    onSubmit?: (formData: IAuditNoteFormData, observationSchema?: IFormSchema) => void;
    submitButton: React.RefObject<HTMLButtonElement>;
    relatedObservation: IObservation["id"] | null;
}

export const EditableAuditNoteForm: React.FC<EditableAuditNoteFormProps> = ({
    initialFormData,
    auditFormType,
    observationFormType,
    onSubmit,
    submitButton,
    relatedObservation,
}) => {
    const texts = getTextProps(useTranslation().t);
    const toast = useNotification();

    const [formData, setFormData] = useState<IAuditNoteFormData>(initialFormData);

    const updateFormData = (values: IAuditNoteFormData) => {
        console.info("updateFormData", { values });

        setFormData((prev) => ({ ...prev, ...values }));
    };

    const submitNote = async (values: IAuditNoteFormData, observationSchema?: IFormSchema) => {
        console.info("submitNote", { values, observationSchema });
        onSubmit && onSubmit(values, observationSchema);
    };

    const handleFormError = (error: Error) => {
        console.log(error.message);

        updateFormData({ makeObservation: false });
        toast.error(texts.failedToFetchObservationForm);
    };

    const contentProps: EditableAuditNoteFormContentProps = {
        observationFormType,
        onUpdateFormData: updateFormData,
        formData,
        auditFormType,
        submitButton,
        onSubmit: submitNote,
        hasRelatedObservation: Boolean(relatedObservation),
        initialFormData,
        ...texts,
    };


    if (formData.makeObservation && observationFormType) {
        return (
            <ErrorBoundary
                clearWhenChanges={formData.makeObservation}
                onError={handleFormError}
                fallback={<Message content={texts.failedToFetchObservationForm} negative />}
            >
                <RenderObservationFormDefinition
                    formReference={observationFormType}
                    render={({ formDefinition }) => (
                        <EditableAuditNoteFormContent
                            {...contentProps}
                            observationFormDefinition={formDefinition || undefined}
                        />
                    )}
                />
            </ErrorBoundary>
        );
    }

    return <EditableAuditNoteFormContent {...contentProps} />;
};
